import { AlertCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ParsedOrder } from "@/lib/bulkOrderUtils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/contexts/LanguageContext";

interface RejectedOrdersListProps {
  orders: ParsedOrder[];
  onRestoreAll: () => void;
  onRestore: (index: number) => void;
}

export function RejectedOrdersList({ orders, onRestoreAll, onRestore }: RejectedOrdersListProps) {
  const { t } = useLanguage();
  const rejectedOrders = orders
    .map((order, index) => ({ order, index }))
    .filter(({ order }) => order.rowState === 'rejected');

  if (rejectedOrders.length === 0) {
    return null;
  }

  return (
    <div className="border border-red-200 dark:border-red-800 rounded-sm overflow-hidden">
      <div className="bg-red-50 dark:bg-red-900/20 p-4 border-b border-red-200 dark:border-red-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-100">
                {t(rejectedOrders.length === 1 ? "bulk.rejected.count" : "bulk.rejected.countPlural").replace('{{count}}', String(rejectedOrders.length))}
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                {t("bulk.rejected.title")}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRestoreAll}
            className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {t("bulk.rejected.restoreAll")}
          </Button>
        </div>
      </div>

      <ScrollArea className="max-h-[200px]">
        <div className="p-4 space-y-2">
          {rejectedOrders.map(({ order, index }) => (
            <div
              key={index}
              className="flex items-start justify-between p-3 bg-background rounded border border-red-200 dark:border-red-800"
            >
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold">
                    Order #{order.orderNumber || 'N/A'}
                  </span>
                  {order.isDuplicate && (
                    <Badge variant="outline" className="text-xs border-orange-500">
                      {t("bulk.statuses.duplicate")}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {order.customerName} • {order.products.length} items
                </p>
                {order.errors.length > 0 && (
                  <div className="text-xs text-red-600 dark:text-red-400 space-y-0.5">
                    {order.errors.map((error, errorIdx) => (
                      <div key={errorIdx}>
                        • {error.message}
                      </div>
                    ))}
                  </div>
                )}
                {order.warnings.length > 0 && (
                  <div className="text-xs text-yellow-600 dark:text-yellow-400 space-y-0.5">
                    {order.warnings.map((warning, warnIdx) => (
                      <div key={warnIdx} className="flex items-start gap-1">
                        <span>•</span>
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRestore(index)}
                className="shrink-0"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}